// The extension identifier is the SINGLE source for every prefixed name the
// extension owns: org variables, the Zoho OAuth connection, the `connname`
// header, and Deluge function names. Change it here and everything derives.
//
// DEVIATION FROM SPEC §1: the spec calls for `easypostgresforzohocrm`. We keep
// `easypostgresqlsync` deliberately — the extension is already installed and
// authenticated in a live org, and Zoho namespaces org variables by this
// prefix. Renaming it orphans the stored API key, organization id and DB
// connection URL, forcing every existing customer to re-authenticate and
// reconnect their database. Rename only as a planned migration.

export const EXTENSION_IDENTIFIER =
  process.env.REACT_APP_EXTENSION_IDENTIFIER || "easypostgresqlsync";

// Zoho prefixes everything an extension owns with `{identifier}__`.
export const orgVarName = (suffix) => `${EXTENSION_IDENTIFIER}__${suffix}`;

// Suffixes only — never spell the prefixed form inline in a component.
//
// This is the full set registered on the extension. Note spec §7 lists only
// the first five; LOG_ERRORS and ZUID exist in the real org and are confirmed
// by the extension's variable list.
export const ORG_VARS = {
  API_KEY: "API_KEY",
  ORGANIZATION_ID: "Organization_ID",
  ZAPI_KEY: "ZAPI_Key",
  DATA_CENTER_URL: "Data_Center_URL",
  DB_CONNECTION_URL: "DB_Connection_URL",
  LOG_ERRORS: "Log_Errors",
  ZUID: "ZUID",
};

// Fully-qualified names, ready to hand to the SDK.
export const ORG_VAR_NAMES = {
  API_KEY: orgVarName(ORG_VARS.API_KEY),
  ORGANIZATION_ID: orgVarName(ORG_VARS.ORGANIZATION_ID),
  ZAPI_KEY: orgVarName(ORG_VARS.ZAPI_KEY),
  DATA_CENTER_URL: orgVarName(ORG_VARS.DATA_CENTER_URL),
  DB_CONNECTION_URL: orgVarName(ORG_VARS.DB_CONNECTION_URL),
  LOG_ERRORS: orgVarName(ORG_VARS.LOG_ERRORS),
  ZUID: orgVarName(ORG_VARS.ZUID),
};

// Every org variable the extension owns.
export const ALL_ORG_VAR_NAMES = Object.values(ORG_VAR_NAMES);

// The five the settings page needs to boot. Kept separate from
// ALL_ORG_VAR_NAMES so the critical path stays a single small read — the
// page cannot render without these, whereas LOG_ERRORS and ZUID are
// situational.
export const CORE_ORG_VAR_NAMES = [
  ORG_VAR_NAMES.API_KEY,
  ORG_VAR_NAMES.ORGANIZATION_ID,
  ORG_VAR_NAMES.ZAPI_KEY,
  ORG_VAR_NAMES.DATA_CENTER_URL,
  ORG_VAR_NAMES.DB_CONNECTION_URL,
];

// The OAuth connection the extension registers with Zoho. Calls that act as
// the authenticated CRM user send THIS as `connname`; everything else sends
// the bare identifier (see CONN_NAME).
export const ZOHO_CONNECTION_NAME = orgVarName("zoho");

// `connname` header for backend calls, and the value the v3 job list scopes
// jobs by together with orgid (§8.2 — a job stored without it is invisible).
export const CONN_NAME = EXTENSION_IDENTIFIER;

// Deluge function names are the identifier + a LOWERCASED function name.
// Getting the casing wrong means the scheduler hits a 404 forever (§12.9).
export const delugeFunctionName = (fn) =>
  `${EXTENSION_IDENTIFIER}__${String(fn).toLowerCase()}`;

export const DELUGE_FUNCTIONS = {
  SYNC: delugeFunctionName("syncModuleData"),
  BACKFILL: delugeFunctionName("backfillModuleData"),
  RESTORE: delugeFunctionName("restoreModuleData"),
};

// The custom CRM module run logs are written to (§9.4).
export const LOGS_MODULE = orgVarName("Logs");

// Zoho stores "not set" as the literal four-character string "null", which is
// NOT the same as a JS null and trips every naive truthiness check (§12.7).
export const isOrgVarUnset = (value) =>
  value === undefined ||
  value === null ||
  value === "" ||
  value === "null" ||
  value === "undefined";

// Normalizes an org-variable read to either a usable string or null.
export const orgVarValue = (value) => (isOrgVarUnset(value) ? null : value);
